import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Building2, Home, Hammer, X, Check, CreditCard,
  DollarSign, Calendar, Percent, ArrowRight, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOAN_ICONS = {
  business: Building2,
  property: Home,
  construction: Hammer
};

function RecurringPaymentPrompt({ invoice, onClose, onAdded }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    payment_type: invoice.extracted_data?.detected_payment_type || 'rent',
    name: invoice.extracted_data?.vendor_name || '',
    amount: invoice.extracted_data?.total_amount || '',
    due_day: invoice.extracted_data?.due_date ? new Date(invoice.extracted_data.due_date).getDate() : 1,
    frequency: invoice.extracted_data?.payment_frequency || 'monthly',
    loan_type: invoice.extracted_data?.detected_loan_type || 'business',
    interest_rate: invoice.extracted_data?.interest_rate || '',
    remaining_balance: invoice.extracted_data?.remaining_balance || '',
    notes: ''
  });

  const isLoan = formData.payment_type === 'loan';
  const LoanIcon = LOAN_ICONS[formData.loan_type] || Building2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        payment_type: formData.payment_type,
        name: formData.name,
        amount: parseFloat(formData.amount),
        due_day: parseInt(formData.due_day),
        frequency: formData.frequency,
        notes: formData.notes
      };

      if (isLoan) {
        payload.loan_type = formData.loan_type;
        if (formData.interest_rate) payload.interest_rate = parseFloat(formData.interest_rate);
        if (formData.remaining_balance) payload.remaining_balance = parseFloat(formData.remaining_balance);
      }

      await axios.post(`${API}/payments`, payload, { withCredentials: true });
      toast.success(`${isLoan ? 'Loan' : 'Rent'} payment added successfully!`);
      onAdded?.();
      onClose();
    } catch (error) {
      console.error('Failed to add payment:', error);
      toast.error('Failed to add recurring payment');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    toast.info('Invoice saved without adding as recurring payment');
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    Recurring Payment Detected!
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    This looks like a {invoice.extracted_data?.detected_payment_type === 'loan' ? 'loan statement' : 'rent invoice'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Detected Info */}
            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Extracted from invoice:</p>
              <div className="flex items-center justify-between">
                <span className="text-slate-700 dark:text-slate-300">{invoice.extracted_data?.vendor_name || 'Unknown'}</span>
                <span className="font-bold text-slate-900 dark:text-white">
                  ${invoice.extracted_data?.total_amount?.toLocaleString() || '0'}
                </span>
              </div>
            </div>

            {/* Payment Type Toggle */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Payment Type
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, payment_type: 'loan' })}
                  className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                    formData.payment_type === 'loan'
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`}
                >
                  <Building2 className="w-5 h-5" />
                  <span className="font-medium">Loan</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, payment_type: 'rent' })}
                  className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                    formData.payment_type === 'rent'
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`}
                >
                  <Home className="w-5 h-5" />
                  <span className="font-medium">Rent</span>
                </button>
              </div>
            </div>

            {/* Loan Type (if loan) */}
            {isLoan && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Loan Type
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'business', label: 'Business', icon: Building2 },
                    { value: 'property', label: 'Property', icon: Home },
                    { value: 'construction', label: 'Construction', icon: Hammer }
                  ].map(loan => (
                    <button
                      key={loan.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, loan_type: loan.value })}
                      className={`p-2 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                        formData.loan_type === loan.value
                          ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                      }`}
                    >
                      <loan.icon className="w-5 h-5" />
                      <span className="text-xs font-medium">{loan.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Name & Amount */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Amount
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full pl-9 pr-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Due Day & Frequency */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Due Day
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={formData.due_day}
                  onChange={(e) => setFormData({ ...formData, due_day: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Frequency
                </label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white"
                >
                  <option value="weekly">Weekly</option>
                  <option value="bi-weekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
            </div>

            {/* Loan-specific fields */}
            {isLoan && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Interest Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.interest_rate}
                    onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                    placeholder="e.g., 8.5"
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Remaining Balance
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="number"
                      value={formData.remaining_balance}
                      onChange={(e) => setFormData({ ...formData, remaining_balance: e.target.value })}
                      className="w-full pl-9 pr-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleSkip}
                className="flex-1 px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg font-medium hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300"
              >
                Skip
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Add as Recurring
                  </>
                )}
              </button>
            </div>

            <p className="text-xs text-center text-slate-500 dark:text-slate-400">
              The invoice has already been saved. This will also track it as a recurring payment.
            </p>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default RecurringPaymentPrompt;
